import { createSelector } from '@reduxjs/toolkit';

import type { AIVoiceFilters } from './ai-voices-slices';

import type { AIVoice } from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { RootState } from 'js/src/libs/RootState';

function getAIVoicesSliceState(state: RootState) {
    return state.aiVoicesAndAvatars.aiVoices;
}

export function getAIVoicesState(state: RootState) {
    return getAIVoicesSliceState(state).data.voices;
}

function getAIVoicesRequestState(state: RootState) {
    return getAIVoicesSliceState(state).request;
}

function getAIVoicesFiltersState(state: RootState) {
    return getAIVoicesSliceState(state).data.filters;
}

function getAIVoicesStoreORM(aiVoicesState: ReturnType<typeof getAIVoicesState>) {
    const orm = new PlainJsStoreORM(aiVoicesState);
    return orm;
}

/**
 * @deprecated
 */
export function getAIVoiceIdsFromAIVoicesState(
    aiVoicesState: ReturnType<typeof getAIVoicesState>,
): string[] {
    const orm = getAIVoicesStoreORM(aiVoicesState);
    return orm.allIds;
}

/**
 * @deprecated
 */
export function getAIVoiceByIdFromAIVoicesState(
    aiVoicesState: ReturnType<typeof getAIVoicesState>,
    id: string,
): AIVoice | null {
    const orm = getAIVoicesStoreORM(aiVoicesState);
    const voice = orm.getById(id);
    return voice || null;
}

export function getAIVoiceIds(state: RootState): string[] {
    return getAIVoicesStoreORM(getAIVoicesState(state)).allIds;
}

export function getAIVoiceById(state: RootState, id: string): AIVoice | null {
    return getAIVoicesStoreORM(getAIVoicesState(state)).getById(id) || null;
}

const isDefinedVoice = (v: AIVoice | undefined): v is AIVoice => v !== undefined;

export const getAIVoices = createSelector(getAIVoicesState, (voicesState): AIVoice[] => {
    const orm = getAIVoicesStoreORM(voicesState);
    return orm.allIds.map((id) => orm.getById(id)).filter(isDefinedVoice);
});

export const getPrivateAIVoices = createSelector(getAIVoices, (voices): AIVoice[] => {
    return voices.filter((v) => v.is_private);
});

export const getAvailableAIVoices = createSelector(getAIVoices, (voices): AIVoice[] => {
    return voices.filter((v) => !v.is_private && !v.is_deprecated);
});

export const getDeprecatedAIVoices = createSelector(getAIVoices, (voices): AIVoice[] => {
    return voices.filter((v) => !v.is_private && v.is_deprecated);
});

export function getAIVoiceFilters(state: RootState): AIVoiceFilters {
    return getAIVoicesFiltersState(state);
}

export function getAIVoicesRequestIsUninitialized(state: RootState): boolean {
    return getAIVoicesRequestState(state).status === 'uninitialized';
}

export function getAIVoicesRequestIsPending(state: RootState): boolean {
    return getAIVoicesRequestState(state).status === 'pending';
}

export function getAIVoicesRequestIsSuccess(state: RootState): boolean {
    return getAIVoicesRequestState(state).status === 'fulfilled';
}

export function getAIVoicesRequestIsError(state: RootState): boolean {
    return getAIVoicesRequestState(state).status === 'rejected';
}

export function getAIVoicesRequestError(state: RootState): string | null {
    return getAIVoicesRequestState(state).error;
}
